import { rpc } from './ws';
import { ApiError, rpcErrorCode } from './api';
import type { CardPaymentClaim } from '@asia/shared';

export interface TransferRow {
  id: string;
  chain: string;
  token: string;
  direction: string;
  fromAddress: string;
  toAddress: string;
  amount: string;
  feeUsd: string;
  status: string;
  txHash: string | null;
  feeTxHash?: string | null;
  failureReason: string | null;
  createdAt: string;
}

export interface TransferPlan {
  transfer: TransferRow;
  mode: 'client-tron-dual-transfer' | 'client-tron-single-transfer';
  treasury: string;
  feeUsd: number;
  /** Network fee before a card-payment discount. */
  fullFeeUsd?: number;
  /** Network fee paid from the card product's margin. */
  subsidyUsd?: number;
  needsApproval: boolean;
  needsPreparation: boolean;
  /** Server already holds a live signed payload — client may broadcast without re-sign. */
  signedReady?: boolean;
  preparedOwner: string;
  preparedRecipient: string;
  preparedAmount: string;
  preparedToken: string;
}

export interface FeeQuote {
  /** Our service fee in USD (0 for TRX — we take no cut on native sends). */
  feeUsd: number;
  /** What this send will charge. Strict pricing; falls back to feeUsd. */
  chargeUsd?: number;
  /** What must remain in the wallet besides the amount (exact fee — no headroom). */
  reserveUsd?: number;
  /** Admin-editable flat fee used when live quoting fails (default $2). */
  feeFallbackUsd?: number;
  /** Network fee before a card-payment discount. */
  fullFeeUsd?: number;
  /** Network fee paid from the card product's margin (+ warm gift). */
  subsidyUsd?: number;
  subsidyPct?: number;
  warmCoveredUsd?: number;
  /** Top-up amount from which the network fee is fully covered by the % discount. */
  freeFromUsd?: number | null;
  /** TRX only: what the CHAIN takes (bandwidth + new-account activation). */
  networkFeeTrx?: number;
  /** TRX only: true when this send also activates a brand-new recipient account. */
  activatesRecipient?: boolean;
}

export const feeQuoteApi = (
  token: string,
  to?: string,
  from?: string,
  amount?: string,
  cardPayment?: CardPaymentClaim,
) => rpc<FeeQuote>('transfers.feeQuote', { token, to, from, amount, cardPayment });

export const createTransferApi = (params: {
  walletId: string;
  token: string;
  to: string;
  amount: string;
  idempotencyKey: string;
  reauthToken?: string;
  cardPayment?: CardPaymentClaim;
}) => rpc<TransferPlan>('transfers.create', params);

export const prepareTransferApi = (id: string, reauthToken?: string) =>
  rpc<TransferPlan>('transfers.prepare', { id, reauthToken });

export const broadcastTransferApi = (
  id: string,
  signedTransaction?: unknown,
  reauthToken?: string,
  signedFeeTransaction?: unknown,
) =>
  rpc<{ transfer: TransferRow; verification?: 'pending' }>('transfers.broadcast', {
    id,
    signedTransaction,
    signedFeeTransaction,
    reauthToken,
  });

/** Native TRX send — client signs a TransferContract, server broadcasts it. */
export const sendTrxApi = (params: {
  walletId: string;
  to: string;
  amount: string;
  idempotencyKey: string;
  signedTransaction: unknown;
  reauthToken?: string;
}) => rpc<{ transfer: TransferRow; verification?: 'pending' }>('transfers.sendTrx', params);

export const listTransfersApi = () => rpc<{ transfers: TransferRow[] }>('transfers.list').then((r) => r.transfers);

/** Re-reads (and reconciles) one transfer — used to wait for on-chain confirm. */
export const getTransferApi = (id: string) =>
  rpc<{ transfer: TransferRow }>('transfers.get', { id }).then((r) => r.transfer);

const TRANSIENT_RPC = new Set(['timeout', 'ws_timeout', 'ws_closed']);

/** The chain definitively rejected/dropped the transfer; unlike a timeout this
 * means a retained payment attempt may be discarded and paid again. */
export class TransferSettlementError extends ApiError {
  readonly terminal = true;
}

/** Wait for a broadcast payment to become final. The transfer id is already
 * durable before this starts, so callers can retain it and safely resume rather
 * than paying again if the connection drops. */
export async function waitForTransferSettlement(
  id: string,
  timeoutMs = 75_000,
): Promise<TransferRow> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    try {
      const transfer = await getTransferApi(id);
      if (transfer.status === 'confirmed') return transfer;
      if (transfer.status === 'failed') {
        throw new TransferSettlementError(409, transfer.failureReason ?? 'transfer_failed');
      }
    } catch (error) {
      if (!TRANSIENT_RPC.has(rpcErrorCode(error))) throw error;
    }
    await new Promise((resolve) => setTimeout(resolve, 1_500));
  }
  throw new ApiError(409, 'payment_not_confirmed');
}
